import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Ear,
  Hand,
  Compass,
  Handshake,
  Building2,
  HeartHandshake,
  Sparkles,
} from "lucide-react";
import { usePageSEO } from "@/lib/seo";
import { PROGRAMS } from "@/lib/programs";

/* ------------------------------------------------------------------ */
/* Homepage — Stichting FIN                                           */
/* "Perspectief begint vandaag." — brede maatschappelijke stichting.   */
/* ------------------------------------------------------------------ */

const principes = [
  {
    icon: Ear,
    label: "Luisteren",
    body: "We beginnen bij het verhaal achter de situatie. Zonder oordeel, zonder standaardformulier.",
  },
  {
    icon: Hand,
    label: "Doen",
    body: "We helpen praktisch bij de stappen die nodig zijn — administratie, gesprekken, planning.",
  },
  {
    icon: Compass,
    label: "Vooruitkijken",
    body: "We werken aan oplossingen die ook op langere termijn verschil maken. Perspectief in plaats van pleisters.",
  },
];

const route = [
  { t: "1", label: "Vertel ons wat er speelt", desc: "Een laagdrempelig eerste gesprek. Geen dossierdrempel." },
  { t: "2", label: "Samen overzicht creëren", desc: "Wat speelt er nu — en wat heeft prioriteit?" },
  { t: "3", label: "Een praktisch plan", desc: "Geen standaardtraject, maar concrete stappen die passen." },
  { t: "4", label: "Samen in beweging", desc: "Waar nodig helpen we bij uitvoering en contact met andere organisaties." },
  { t: "5", label: "Verder kunnen", desc: "Het doel is meer grip, meer keuze en meer zelfstandigheid." },
];

const partnerships = [
  {
    icon: HeartHandshake,
    label: "Doorverwijzen",
    body: "Heeft u iemand in beeld die praktische ondersteuning nodig heeft? Wij nemen laagdrempelig contact op.",
  },
  {
    icon: Building2,
    label: "Projectpartnerschap",
    body: "Samen een maatschappelijk project ontwikkelen of uitvoeren — van pilot tot opschaling.",
  },
  {
    icon: Sparkles,
    label: "Financiering & impact",
    body: "Bijdragen aan concrete maatschappelijke impact via projecten binnen onze vier programma's.",
  },
];

const impactCategories = [
  { label: "Mensen", body: "Meer grip, overzicht en perspectief." },
  { label: "Gezinnen", body: "Meer stabiliteit en ruimte om vooruit te kijken." },
  { label: "Gemeenschappen", body: "Investeren in kansen en een sterke leefomgeving." },
];

export default function HomePage() {
  usePageSEO({
    title: "Stichting FIN — Perspectief begint vandaag.",
    description:
      "Stichting FIN helpt mensen en gemeenschappen die door financiële, sociale of maatschappelijke omstandigheden zijn vastgelopen. Praktische ondersteuning, persoonlijke begeleiding en maatschappelijke projecten in Rotterdam en daarbuiten.",
  });

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-petrol text-cream" data-testid="home-hero">
        <div aria-hidden="true" className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1531983412531-1f49a365ffed?auto=format&fit=crop&w=2000&q=80"
            alt=""
            className="w-full h-full object-cover opacity-40"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-petrol via-petrol/95 to-petrol/60" />
          <div className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        </div>

        <div className="container-x relative z-10 pt-20 md:pt-28 pb-24 md:pb-32 max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-cream/10 backdrop-blur border border-cream/15 rounded-full px-4 py-1.5 mb-8">
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-cream/90">
              Stichting FIN — Future Is Now
            </span>
          </div>
          <h1 className="font-display font-semibold text-[clamp(2.6rem,7vw,5.4rem)] leading-[1.02] tracking-tight text-balance text-cream">
            Perspectief begint <em className="italic text-gold">vandaag</em>.
          </h1>
          <p className="mt-7 text-lg md:text-[19px] leading-relaxed text-cream/85 max-w-2xl text-pretty">
            Stichting FIN helpt mensen en gemeenschappen die door financiële, sociale of maatschappelijke omstandigheden zijn
            vastgelopen. Met praktische ondersteuning, persoonlijke begeleiding en maatschappelijke projecten werken we aan
            bestaanszekerheid, zelfredzaamheid en gelijke kansen.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/hulp-nodig"
              className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-7 py-3.5 text-[15px] font-semibold shadow-gold"
              data-testid="hero-cta-hulp"
            >
              Ik zoek hulp <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/projecten"
              className="inline-flex items-center gap-2 bg-cream/10 backdrop-blur border border-cream/20 text-cream hover:bg-cream/15 transition rounded-full px-7 py-3.5 text-[15px] font-semibold"
              data-testid="hero-cta-projecten"
            >
              Ontdek onze projecten
            </Link>
            <Link
              to="/voor-gemeenten"
              className="inline-flex items-center gap-2 text-cream/85 hover:text-gold transition px-4 py-3.5 text-[15px] font-semibold"
              data-testid="hero-cta-partners"
            >
              Samenwerken met FIN <ArrowRight size={15} />
            </Link>
          </div>
        </div>

        {/* Trust bar */}
        <div className="relative z-10 border-t border-cream/10 bg-petrolDark/60 backdrop-blur">
          <div className="container-x py-5 flex flex-wrap items-center justify-between gap-4">
            <p className="text-cream/70 text-[13.5px] uppercase tracking-[0.24em] font-bold">
              Praktisch · Persoonlijk · Toekomstgericht
            </p>
            <p className="text-cream/60 italic text-[14.5px] font-display max-w-lg">
              "Soms is één iemand die naast je gaat staan het begin van verandering."
            </p>
          </div>
        </div>
      </section>

      {/* ============ WAAROM FIN ============ */}
      <section className="py-20 md:py-28 bg-cream" data-testid="home-waarom">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden shadow-card">
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=80"
                alt="Een begeleider en cliënt kijken samen naar papieren aan een lichte tafel"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-petrol/25 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Waarom FIN?</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance text-petrol">
              Soms heb je geen ingewikkeld systeem nodig. Je hebt iemand nodig die <em className="italic text-gold">naast je staat</em>.
            </h2>
            <p className="mt-6 text-[17px] leading-[1.75] text-inkMuted text-pretty">
              Door schulden, overheidsproblemen, verlies van inkomen, complexe administratie of andere gebeurtenissen kunnen mensen
              het overzicht verliezen. FIN kijkt niet alleen naar het probleem, maar naar de totale situatie.
            </p>
            <div className="mt-8 space-y-4">
              {principes.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.label} className="flex items-start gap-4" data-testid={`principe-${p.label.toLowerCase()}`}>
                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-goldSoft text-petrol shrink-0">
                      <Icon size={20} strokeWidth={1.75} />
                    </span>
                    <div>
                      <p className="font-display text-xl text-petrol leading-tight mb-1">{p.label}</p>
                      <p className="text-inkMuted text-[15px] leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROGRAMMA'S ============ */}
      <section className="py-20 md:py-28 bg-sand" data-testid="home-programmas">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Onze programma's</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Eén missie. <em className="italic text-gold">Verschillende manieren</em> om verschil te maken.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-inkMuted text-pretty">
              Onze projecten verschillen, maar hebben één ding gemeen: mensen en gemeenschappen sterker achterlaten dan we ze
              aantroffen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            {PROGRAMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.key}
                  className="group relative bg-ivory border border-line rounded-3xl overflow-hidden flex flex-col hover:shadow-card transition-all duration-500"
                  data-testid={`programma-${p.key}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.imageAlt}
                      className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-petrol/60 via-petrol/10 to-transparent" />
                    <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-cream/95 backdrop-blur rounded-full px-3.5 py-1.5">
                      <Icon size={15} className="text-petrol" strokeWidth={2} />
                      <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-petrol">{p.label}</span>
                    </div>
                  </div>
                  <div className="p-7 md:p-8 flex flex-col flex-1">
                    <h3 className="font-display text-2xl md:text-3xl text-petrol leading-tight mb-2">{p.subtitle}</h3>
                    <p className="text-inkMuted leading-relaxed text-[15px] text-pretty flex-1 mb-5">{p.short}</p>
                    <div className="flex flex-wrap gap-3">
                      <Link
                        to={`/wat-we-doen#${p.anchorId}`}
                        className="inline-flex items-center gap-1.5 text-petrol font-semibold text-sm hover:text-gold transition"
                        data-testid={`programma-cta-${p.key}`}
                      >
                        Meer over {p.label} <ArrowRight size={14} />
                      </Link>
                      <span className="text-line">·</span>
                      <Link
                        to={`/projecten?thema=${p.key}`}
                        className="inline-flex items-center gap-1.5 text-gold font-semibold text-sm hover:text-goldDark transition"
                      >
                        Bekijk projecten <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TOESLAGENAFFAIRE ============ */}
      <section className="py-20 md:py-28 bg-petrol text-cream relative overflow-hidden" data-testid="home-toeslagen">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-32 -left-24 w-[400px] h-[400px] rounded-full bg-goldSoft/10 blur-3xl" />
        <div className="container-x relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Toeslagenaffaire</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">
              Herstel is meer dan een <em className="italic text-gold">financiële vergoeding</em>.
            </h2>
            <div className="mt-7 space-y-4 text-cream/85 text-[17px] leading-[1.75] max-w-2xl text-pretty">
              <p>
                De toeslagenaffaire heeft bij veel gezinnen gevolgen achtergelaten die niet verdwijnen met één beschikking of
                betaling. Administratie, schulden, wonen, werk, gezin en vertrouwen in instanties kunnen allemaal geraakt zijn.
              </p>
              <p>
                FIN wil gedupeerde ouders praktisch ondersteunen bij het opnieuw creëren van overzicht en het zetten van concrete
                stappen richting herstel.
              </p>
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/hulp-nodig"
                className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-6 py-3 text-sm font-semibold shadow-gold"
                data-testid="toeslagen-cta-hulp"
              >
                Hulp bij herstel <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/voor-gemeenten"
                className="inline-flex items-center gap-2 bg-cream/10 border border-cream/20 text-cream hover:bg-cream/15 transition rounded-full px-6 py-3 text-sm font-semibold"
                data-testid="toeslagen-cta-doorverwijzen"
              >
                Ik wil iemand doorverwijzen
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden shadow-card border border-cream/10">
              <img
                src="https://images.unsplash.com/photo-1590650046871-92c887180603?auto=format&fit=crop&w=1200&q=80"
                alt="Een ouder krijgt weer overzicht — administratie en documenten aan een keukentafel"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-petrol/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOE WIJ HELPEN ============ */}
      <section className="py-20 md:py-28 bg-cream" data-testid="home-hoe">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Hoe wij helpen</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Van eerste gesprek <em className="italic text-gold">naar verder kunnen</em>.
            </h2>
          </div>
          <ol className="grid gap-4 md:gap-5">
            {route.map((r, i) => (
              <li
                key={r.t}
                className="grid md:grid-cols-12 gap-5 items-center bg-sandSoft border border-line rounded-3xl p-6 md:p-7"
                data-testid={`route-stap-${r.t}`}
              >
                <div className="md:col-span-2 flex items-center gap-4">
                  <span className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-petrol text-cream font-display italic text-2xl shrink-0">
                    {r.t}
                  </span>
                  {i < route.length - 1 && (
                    <ArrowRight size={20} className="text-gold/60 hidden md:block" aria-hidden="true" />
                  )}
                </div>
                <div className="md:col-span-4">
                  <p className="font-display text-2xl text-petrol leading-tight">{r.label}</p>
                </div>
                <div className="md:col-span-6">
                  <p className="text-inkMuted text-[15.5px] leading-relaxed">{r.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============ VOOR GEMEENTEN & PARTNERS ============ */}
      <section className="py-20 md:py-28 bg-sand" data-testid="home-partners">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Voor gemeenten & partners</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Samen bereiken we <em className="italic text-gold">meer</em>.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-inkMuted text-pretty max-w-2xl">
              FIN werkt samen met gemeenten, welzijnsorganisaties, woningcorporaties, zorgorganisaties, fondsen en bedrijven —
              op preventie, herstel, bestaanszekerheid, participatie en community-projecten.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {partnerships.map((p) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.label}
                  className="bg-ivory border border-line rounded-3xl p-7 hover:shadow-card transition"
                  data-testid={`partnership-${p.label.toLowerCase().replace(/[^\w]+/g, "-")}`}
                >
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-goldSoft text-petrol mb-4">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <h3 className="font-display text-2xl text-petrol leading-tight mb-2">{p.label}</h3>
                  <p className="text-inkMuted leading-relaxed text-[15px] text-pretty">{p.body}</p>
                </article>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/voor-gemeenten"
              className="inline-flex items-center gap-2 bg-petrol text-cream hover:bg-petrolDark transition rounded-full px-6 py-3 text-sm font-semibold shadow-cta"
              data-testid="partners-cta-samenwerken"
            >
              Samenwerken met Stichting FIN <ArrowUpRight size={15} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gold text-petrol hover:bg-goldSoft transition rounded-full px-6 py-3 text-sm font-semibold shadow-gold"
              data-testid="partners-cta-kennismaking"
            >
              Plan een kennismaking <Handshake size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ IMPACT (transparant, zonder cijfers) ============ */}
      <section className="py-20 md:py-24 bg-cream" data-testid="home-impact">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-4">Impact</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight text-balance text-petrol">
              Impact die <em className="italic text-gold">verder gaat</em> dan vandaag.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-inkMuted text-pretty">
              We delen pas concrete cijfers wanneer die er zorgvuldig zijn. Waar we op focussen, kunnen we vandaag al toelichten.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {impactCategories.map((c) => (
              <article
                key={c.label}
                className="bg-sandSoft border border-line rounded-3xl p-8 text-center"
                data-testid={`impact-${c.label.toLowerCase()}`}
              >
                <p className="font-display text-3xl text-petrol leading-tight mb-3">{c.label}</p>
                <p className="text-inkMuted text-[15px] leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
